/**
 * La validation d'un bon d'intervention, dite en français : le récapitulatif avant confirmation, et ce qui s'est
 * écrit après (lot I1.2, ADR-0014).
 *
 * Aucune règle métier ici : le serveur annonce ce qu'il écrira — il joue la VRAIE validation dans une transaction
 * qu'il annule, donc l'annonce ne peut pas diverger de l'écriture. L'écran ne fait que la mettre en phrases.
 *
 * Fonctions pures, sans dépendance au navigateur.
 */
import type { ApercuValidationBon, BonDetail, ClotureBon, FacturationBon, PointagesJour } from '../api/client';
import { valeurAffichee } from './bon-edition';
import { jourFr } from './interventions';

export interface LigneApercu {
  readonly texte: string;
  /** `attention` : un point à lire (relevé signalé), en couleur ; la validation reste possible. */
  readonly gravite: 'info' | 'attention';
}

const pluriel = (n: number, un: string, plusieurs: string): string => `${n} ${n > 1 ? plusieurs : un}`;

/** Le récapitulatif avant confirmation, une phrase par effet. */
export function lignesApercu(a: ApercuValidationBon): readonly LigneApercu[] {
  const lignes: LigneApercu[] = [];

  for (const r of a.readings) {
    const valeur = `${valeurAffichee(r.value)}${r.unit ? ` ${r.unit}` : ''}`;
    lignes.push({
      texte:
        r.action === 'ADOPTER'
          ? `${r.counterLabel} : ${valeur} — remplace votre relevé identique déjà saisi (l’ancien reste dans l’historique du compteur, marqué « remplacé »).`
          : `${r.counterLabel} : ${valeur} — nouveau relevé.`,
      gravite: 'info',
    });
    if (r.anomaly && r.anomalyReason) {
      lignes.push({
        texte: `${r.counterLabel} : relevé signalé « ${r.anomalyReason} ». Il est enregistré quand même ; vous pourrez le rectifier depuis le compteur.`,
        gravite: 'attention',
      });
    }
  }

  if (a.service) {
    const heures = a.service.counterValue ? ` à ${valeurAffichee(a.service.counterValue)} h` : '';
    lignes.push({
      texte:
        a.service.action === 'ADOPTER'
          ? `Entretien réalisé : palier ${a.service.levelCode} le ${jourFr(a.service.performedOn)}${heures} — remplace l’entretien identique déjà saisi (l’ancien reste dans l’historique, marqué « remplacé »).`
          : `Entretien réalisé : palier ${a.service.levelCode} le ${jourFr(a.service.performedOn)}${heures} — nouvel entretien.`,
      gravite: 'info',
    });
  }

  if (a.readings.length === 0 && !a.service) {
    lignes.push({ texte: 'Ce bon ne porte aucun relevé ni entretien : il n’écrit rien dans les compteurs.', gravite: 'info' });
  }

  const gardees: string[] = [];
  if (a.technicians > 0) gardees.push(pluriel(a.technicians, 'technicien', 'techniciens'));
  if (a.checks > 0) gardees.push(pluriel(a.checks, 'contrôle', 'contrôles'));
  if (a.lines > 0) gardees.push(pluriel(a.lines, 'ligne d’article', 'lignes d’article'));
  if (a.followUps > 0) {
    gardees.push(`${pluriel(a.followUps, 'suite à donner', 'suites à donner')} (${a.followUps > 1 ? 'ouvertes' : 'ouverte'})`);
  }
  if (gardees.length > 0) lignes.push({ texte: `Le bon garde aussi : ${gardees.join(', ')}.`, gravite: 'info' });

  return lignes;
}

/** Ce que la validation vient d'écrire, en une phrase (le message de succès de la page). */
export function phraseValidation(bon: Pick<BonDetail, 'number' | 'validated'>): string {
  const v = bon.validated;
  if (v === null) return `Bon ${bon.number} validé.`;

  const ecrit: string[] = [];
  if (v.readings.length > 0) ecrit.push(pluriel(v.readings.length, 'relevé de compteur', 'relevés de compteur'));
  if (v.service) ecrit.push(`l’entretien du palier ${v.service.levelCode}`);
  if (v.checks.length > 0) {
    const faits = v.checks.filter((c) => c.checked).length;
    ecrit.push(`${pluriel(v.checks.length, 'contrôle', 'contrôles')} (${pluriel(faits, 'fait', 'faits')})`);
  }
  if (v.lines.length > 0) ecrit.push(pluriel(v.lines.length, 'ligne d’article', 'lignes d’article'));
  if (v.followUps.length > 0) ecrit.push(pluriel(v.followUps.length, 'suite à donner', 'suites à donner'));

  return ecrit.length === 0
    ? `Bon ${bon.number} validé : il est figé.`
    : `Bon ${bon.number} validé : il est figé. Écrit : ${ecrit.join(', ')}.`;
}

/**
 * La validation peut-elle être confirmée ? Un bon clôturé sur le téléphone sans « Contrat annuel / Facturé à la visite »
 * (`billingRequired`) exige que le bureau choisisse — jamais présélectionné. Rend le champ à envoyer (`undefined` = rien).
 */
export function choixValidation(
  apercu: Pick<ApercuValidationBon, 'billingRequired'> | null,
  choix: FacturationBon | null,
): { readonly confirmable: boolean; readonly billing: FacturationBon | undefined } {
  if (apercu === null) return { confirmable: false, billing: undefined };
  if (!apercu.billingRequired) return { confirmable: true, billing: undefined };
  return choix === null ? { confirmable: false, billing: undefined } : { confirmable: true, billing: choix };
}

/** « 27/10 à 11 h 20 » (dans le fuseau donné, celui du navigateur par défaut). */
export function instantFr(iso: string, fuseau?: string): string {
  const parts = new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    ...(fuseau ? { timeZone: fuseau } : {}),
  }).formatToParts(new Date(iso));
  const v = (t: string): string => parts.find((p) => p.type === t)?.value ?? '';
  return `${v('day')}/${v('month')} à ${v('hour')} h ${v('minute')}`;
}

/**
 * « 27/10/2026 » : le jour seul, pour le papier remis au client, qui ne voit jamais d'heure (Benjamin, 2026-10-27 : « on a dit
 * que l'on ne voulait pas que le client voie les heures, enlève des bons l'heure dans la signature »).
 */
export function jourFrDansFuseau(iso: string, fuseau?: string): string {
  const parts = new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    ...(fuseau ? { timeZone: fuseau } : {}),
  }).formatToParts(new Date(iso));
  const v = (t: string): string => parts.find((p) => p.type === t)?.value ?? '';
  return `${v('day')}/${v('month')}/${v('year')}`;
}

/** La clôture faite sur le téléphone, en une phrase : qui a signé et quand, ou pourquoi le client était absent. */
export function phraseCloture(c: ClotureBon, fuseau?: string): string {
  if (c.absentReason !== null) return `Client absent : ${c.absentReason}`;
  return `Signé par ${c.signerName ?? 'le client'} le ${instantFr(c.signedAt ?? c.closedAt, fuseau)}`;
}

/**
 * Garde-fou des pointages (registre B34, demande de Benjamin du 2026-10-26) : un technicien encore en route, sur site ou en
 * pause n'a pas fini sa journée ; son temps sur site n'est pas enregistré. Dit dans la fenêtre de validation, jamais un refus.
 */
export function pointagesIncomplets(pointages: readonly PointagesJour[]): readonly string[] {
  const etats: Readonly<Record<string, string>> = {
    EN_ROUTE: 'est encore en route (pas de « Je suis arrivé »)',
    SUR_SITE: 'est encore sur site (pas de « Je quitte le site »)',
    EN_PAUSE: 'est encore en pause',
  };
  return pointages
    .filter((p) => etats[p.etat] !== undefined)
    .map((p) => `Pointages incomplets : ${p.technicien.nom} ${etats[p.etat]} le ${jourFr(p.jour)} ; ${p.durees.surSite} min sur site comptées.`);
}
